import React from "react";

/**
 * DNAHelix — an elegant, minimal, precision-engineered double helix.
 *
 * Pure SVG. Two sinusoidal strands (brushed gold + brushed silver) with
 * hairline rungs connecting them at regular intervals — reads like a
 * scientific instrument schematic, never a cartoon.
 *
 * Props:
 *   height       — total pixel height (default 480)
 *   width        — pixel width of the helix (default 80)
 *   opacity      — overall opacity (default 0.28)
 *   turns        — number of full twists across the height (default 4)
 *   className    — pass-through for positioning
 */
export const DNAHelix = ({
    height = 480,
    width = 80,
    opacity = 0.28,
    turns = 4,
    className = "",
}) => {
    const steps = 64;
    const amp = width / 2 - 2;
    const midX = width / 2;

    const gold = [];
    const silver = [];
    const rungs = [];

    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        const y = t * height;
        const phase = t * turns * Math.PI * 2;
        const xg = midX + Math.sin(phase) * amp;
        const xs = midX + Math.sin(phase + Math.PI) * amp;
        gold.push(`${i === 0 ? "M" : "L"} ${xg.toFixed(2)} ${y.toFixed(2)}`);
        silver.push(`${i === 0 ? "M" : "L"} ${xs.toFixed(2)} ${y.toFixed(2)}`);
        // Rung every 4 steps (16 rungs total) — only when the strands
        // aren't overlapping so it reads as a helix, not a ladder.
        if (i % 4 === 0) {
            const sep = Math.abs(xg - xs);
            if (sep > 6) {
                rungs.push({ x1: xg, x2: xs, y });
            }
        }
    }

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            className={className}
            style={{ opacity }}
            aria-hidden="true"
            data-testid="dna-helix"
        >
            <defs>
                <linearGradient id="dna-gold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#F1D68B" stopOpacity="0" />
                    <stop offset="18%"  stopColor="#E6C273" stopOpacity="1" />
                    <stop offset="82%"  stopColor="#C89B4A" stopOpacity="1" />
                    <stop offset="100%" stopColor="#8E682E" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="dna-silver" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#DFE1E3" stopOpacity="0" />
                    <stop offset="18%"  stopColor="#C4C6C8" stopOpacity="1" />
                    <stop offset="82%"  stopColor="#9C9FA2" stopOpacity="1" />
                    <stop offset="100%" stopColor="#74787C" stopOpacity="0" />
                </linearGradient>
            </defs>

            {/* Rungs — hairline gold, drawn first so strands sit on top */}
            {rungs.map((r, i) => (
                <line
                    key={`r${i}`}
                    x1={r.x1}
                    x2={r.x2}
                    y1={r.y}
                    y2={r.y}
                    stroke="url(#dna-gold)"
                    strokeWidth="0.6"
                    strokeOpacity="0.55"
                />
            ))}

            {/* Strand — brushed gold */}
            <path
                d={gold.join(" ")}
                fill="none"
                stroke="url(#dna-gold)"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
            {/* Strand — brushed silver */}
            <path
                d={silver.join(" ")}
                fill="none"
                stroke="url(#dna-silver)"
                strokeWidth="1.2"
                strokeLinecap="round"
            />

            {/* Illuminated nodes at every second rung so it feels engineered */}
            {rungs.filter((_, i) => i % 2 === 0).map((r, i) => (
                <g key={`n${i}`}>
                    <circle cx={r.x1} cy={r.y} r="1.4" fill="#F1D68B" opacity="0.75" />
                    <circle cx={r.x2} cy={r.y} r="1.2" fill="#DFE1E3" opacity="0.55" />
                </g>
            ))}
        </svg>
    );
};

export default DNAHelix;
